import { memo, useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { up } from 'styled-breakpoints';
import {
  Contact,
  MutationCreateContactArgs,
  Query,
  QueryContactArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import {
  ContactFormBottomContent,
  ContactFormFields,
  ContactNameInputs,
  ContactStageAndStatus,
} from 'src/components/domain/contacts/contact-form/contact-form-fields';
import {
  addressDefaultValue,
  CONTACT_AUTOSAVE_ON_BLUR_FIELDS,
  ContactFormValues,
  getContactDefaultValues,
  getPossibleExtraFields,
  PossibleExtraFields,
  useContactFormResolver,
} from 'src/components/domain/contacts/contact-form/contact-form.schema';
import { PossibleDuplicateContactModal } from 'src/components/domain/contacts/contact-form/possible-duplicate-contact-modal';
import {
  getContactUpsertVariables,
  PossibleDuplicateContactState,
  useContactUpsertErrorHandler,
} from 'src/components/domain/contacts/contact-form/use-contact-upsert-error-handler';
import { useUpdateContactAutosave } from 'src/components/domain/contacts/contact-form/use-update-contact-autosave';
import { useCreateContact } from 'src/components/domain/contacts/hooks/use-create-contact';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { InlineFormModal } from 'src/components/general/modals/inline-form/inline-form-modal';
import { useShowCloseConfirmationModal } from 'src/components/general/modals/inline-form/shared';
import { useEntityCreatedNotification } from 'src/components/layout/authenticated-layout/header/add-new/hooks/useEntityCreatedNotification';
import { CONTACT_STAGES } from 'src/components/pages/contacts/contact-queries';
import { GET_CONTACT } from 'src/hooks/gql/use-refresh-contact';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useCurrentUser } from 'src/hooks/use-current-user';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useModalUrl } from 'src/hooks/use-modal-url';
import { useVisible } from 'src/hooks/use-visible';

export const ContactInlineFormModal = memo(() => {
  const currentInstitution = useCurrentInstitution();
  const [extraFieldsToShow, setExtraFieldsToShow] = useState<PossibleExtraFields[]>([]);
  const [stageAndStatus, setStageAndStatus] = useState<ContactStageAndStatus | undefined>(
    undefined,
  );
  const currentUser = useCurrentUser();
  const showCloseConfirmationModal = useShowCloseConfirmationModal();
  const modalVisibilityHandler = useVisible(true);
  const { goBack } = useModalUrl({
    backgroundLocation: `/i/company/${currentInstitution.id}/contacts`,
  });
  const breakpointUpMd = useBreakpoint(up('md'));
  const { t } = useTranslation(['common', 'contacts']);
  const { showContactCreatedNotification } = useEntityCreatedNotification();
  const { contactId } = useParams<{
    contactId?: string;
  }>();
  const isCreatingContact = !contactId;
  const showNotification = useShowNotification();
  const handleGraphQLError = useHandleGraphQLError();
  const resolver = useContactFormResolver();

  const [possibleDuplicateOnCreate, setPossibleDuplicateOnCreate] =
    useState<PossibleDuplicateContactState | null>(null);

  useQuery(CONTACT_STAGES, {
    onCompleted: ({ contactStages }) => {
      const defaultStage = contactStages.find((stage) => stage.slug === 'LEAD');
      const defaultStatus = defaultStage?.statuses.find((status) => status.id === '1');

      if (defaultStage && defaultStatus) {
        setStageAndStatus({ stage: defaultStage, status: defaultStatus });
      }
    },
  });

  const { createContact, isCreatingContact: createContactLoading } = useCreateContact();

  const [defaultValues, setDefaultValues] = useState<ContactFormValues>({
    firstName: '',
    lastName: '',
    primaryEmail: null,
    primaryPhone: null,
    interest: null,
    secondaryPhone: null,
    gender: null,
    birthdate: null,
    campaignId: null,
    sourceId: null,
    statusId: 1,
    ownerId: isCreatingContact ? currentUser?.id ?? null : null,
    tags: [],
    address: addressDefaultValue,
  });

  const contactForm = useForm<ContactFormValues>({
    resolver,
    defaultValues,
    reValidateMode: 'onSubmit',
  });

  const {
    onAutosave,
    isUpdatingContact,
    possibleDuplicateContact: possibleDuplicateOnUpdate,
    onAddAnyway: onAddAnywayOnUpdate,
    closePossibleDuplicateModal: closePossibleDuplicateOnUpdate,
  } = useUpdateContactAutosave({
    form: contactForm,
    contactId: contactId ? Number(contactId) : undefined,
  });

  const { handleUpsertError: handleCreateContactError } = useContactUpsertErrorHandler({
    form: contactForm,
    onPossibleDuplicate: setPossibleDuplicateOnCreate,
    markConflictField: false,
  });

  const handlecloseModal = useCallback(() => {
    goBack();
    modalVisibilityHandler.close();
  }, [goBack, modalVisibilityHandler]);

  const handleContactCreated = (contact: Contact) => {
    showContactCreatedNotification({ message: contact.label, contact: contact });
    handlecloseModal();
  };

  const handleGetContactError = useCallback(() => {
    modalVisibilityHandler.close();
    goBack();
    showNotification({
      title: 'error',
      message: t('contacts:createContactModal.notifications.contactNotFound'),
      type: 'error',
    });
  }, [modalVisibilityHandler, goBack, showNotification, t]);

  const hasLoadedContactRef = useRef(false);

  const { loading: getContactLoading } = useQuery<
    Pick<Query, 'contact'>,
    QueryContactArgs
  >(GET_CONTACT, {
    variables: { id: Number(contactId) },
    skip: !contactId,
    onCompleted: ({ contact }) => {
      if (!contact) {
        handleGetContactError();
        return;
      }

      if (hasLoadedContactRef.current) {
        // onCompleted re-fires whenever Apollo's cache broadcasts a change to this
        // contact's normalized entity — including from our own update/autosave
        // mutations, whose response only returns `id`/`label` (label is derived
        // and always changes). Resetting the form here again would clobber
        // in-progress edits with the stale (non-`label`) cached field values.
        return;
      }
      hasLoadedContactRef.current = true;

      const values = getContactDefaultValues(contact);

      setDefaultValues(values);
      setStageAndStatus({
        stage: contact.stage,
        status: contact.status,
      });
      setExtraFieldsToShow(getPossibleExtraFields(values));
      contactForm.reset(values);
    },
    onError: handleGetContactError,
  });

  const {
    formState: { isDirty, dirtyFields },
  } = contactForm;

  const isFormDirty = isDirty && Object.keys(dirtyFields).length > 0;

  const statusModalTitle = isCreatingContact
    ? t('contacts:createContactModal.title.create')
    : t('contacts:createContactModal.title.update');

  const possibleDuplicateContact = isCreatingContact
    ? possibleDuplicateOnCreate
    : possibleDuplicateOnUpdate;

  return (
    <InlineFormModal<ContactFormValues>
      title={statusModalTitle}
      submitButtonText={t('contacts:createContactModal.actions.create')}
      testId="create-contact-modal"
      isSubmitting={createContactLoading || isUpdatingContact}
      disableSubmitButton={!isFormDirty}
      isLoading={getContactLoading}
      visibilityHandler={modalVisibilityHandler}
      form={contactForm}
      onCancel={() => {
        if (!isFormDirty) {
          handlecloseModal();
          return;
        }

        showCloseConfirmationModal({
          onCancel: handlecloseModal,
        });
      }}
      onSubmit={async (values) => {
        if (!isCreatingContact) {
          return;
        }

        const createContactVariables = {
          allowDuplicates: false,
          institutionId: currentInstitution.id,
          ...getContactUpsertVariables(values),
        };

        await createContact({
          variables: createContactVariables,
          onError: (error) => handleCreateContactError(error, createContactVariables),
          onCompleted: (data) => {
            handleContactCreated(data.createContact.contact!);
          },
        });
      }}
      autoSaveEnabled={!isCreatingContact}
      onAutosave={onAutosave}
      autosaveOnBlurFields={CONTACT_AUTOSAVE_ON_BLUR_FIELDS}
      topContent={<ContactNameInputs form={contactForm} />}
      bottomContent={
        breakpointUpMd ? (
          <ContactFormBottomContent
            form={contactForm}
            stageAndStatus={stageAndStatus}
            onStageAndStatusChange={setStageAndStatus}
            statusModalTitle={statusModalTitle}
          />
        ) : null
      }
    >
      <ContactFormFields
        form={contactForm}
        layout="modal"
        stageAndStatus={stageAndStatus}
        onStageAndStatusChange={setStageAndStatus}
        statusModalTitle={t('contacts:createContactModal.newContact')}
        extraFieldsToShow={extraFieldsToShow}
        onExtraFieldsToShowChange={setExtraFieldsToShow}
        defaultAddress={defaultValues.address}
      />
      {possibleDuplicateContact && (
        <PossibleDuplicateContactModal
          onClose={() => {
            if (isCreatingContact) {
              setPossibleDuplicateOnCreate(null);
            } else {
              closePossibleDuplicateOnUpdate();
            }
          }}
          duplicateData={possibleDuplicateContact.duplicateData}
          contact={possibleDuplicateContact.contact}
          onAddAnyway={async () => {
            if (!isCreatingContact) {
              await onAddAnywayOnUpdate();
              return;
            }

            // Safe: on create, queryVariables.input was built from the fully-validated
            // create-form values (see onSubmit above), never a partial autosave one.
            await createContact({
              variables: {
                ...possibleDuplicateContact.queryVariables,
                allowDuplicates: true,
              } as MutationCreateContactArgs,
              onCompleted: (data) => {
                setPossibleDuplicateOnCreate(null);

                handleContactCreated(data.createContact.contact!);
              },
              onError: handleGraphQLError,
            });
          }}
        />
      )}
    </InlineFormModal>
  );
});
